// frontend/openaiModal.jsx
import React, { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Checkbox,
  Chip,
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from "@material-ui/core";

import { 
  Visibility, 
  VisibilityOff, 
  ExpandMore,
  Add,
  Delete,
  Info,
  Settings,
  Timer,
  Chat
} from "@material-ui/icons";
import { InputAdornment } from "@material-ui/core";

const MAX_REPLY_DELAY_MS = 86399000;
const replyDelayToClock = (value) => {
  const totalSeconds = Math.floor(Math.min(Math.max(Number(value) || 0, 0), MAX_REPLY_DELAY_MS) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map(part => String(part).padStart(2, "0")).join(":");
};
const clockToReplyDelay = (value) => {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || "");
  if (!match) return 0;
  const hours = Math.min(Number(match[1]), 23);
  const minutes = Math.min(Number(match[2]), 59);
  const seconds = Math.min(Number(match[3] || 0), 59);
  return ((hours * 60 + minutes) * 60 + seconds) * 1000;
};

const MAX_BLOCK_GAP_MS = 15000;
const blockGapToClock = (value) => {
  const totalSeconds = Math.round(Math.min(Math.max(Number(value) || 0, 0), MAX_BLOCK_GAP_MS) / 1000);
  return `00:00:${String(totalSeconds).padStart(2, "0")}`;
};
const clockToBlockGap = (value) => {
  const match = /^(\d{2}):(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) return 0;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  return Math.min(Math.max(((hours * 60 + minutes) * 60 + seconds) * 1000, 0), MAX_BLOCK_GAP_MS);
};

// Lista de modelos OpenAI suportados
const openAiModels = [
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-1106", 
  "gpt-3.5-turbo-16k",
  "gpt-4",
  "gpt-4-turbo",
  "gpt-4o",
  "gpt-4o-mini",
  "gpt-4.1",
  "gpt-4.1-mini",
  "gpt-4.1-nano"
];

// Lista de vozes disponíveis para OpenAI
const availableVoices = [
  "texto",
  "pt-BR-FabioNeural",
  "pt-BR-FranciscaNeural",
  "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural",
  "pt-BR-DonatoNeural",
  "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural",
  "pt-BR-HumbertoNeural",
  "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural",
  "pt-BR-LeticiaNeural",
  "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural",
  "pt-BR-ValerioNeural",
  "pt-BR-YaraNeural"
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  flowModeCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  },
  temporarySettings: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px dashed ${theme.palette.primary.light}`
  },
  keywordChip: {
    margin: theme.spacing(0.5),
  },
  helpText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5)
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    }
  },
  accordionSummary: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    }
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column"
  }
}));

// Esquema de validação para OpenAI
const OpenAiSchema = Yup.object().shape({
  name: Yup.string()
    .min(5, "Muito curto!")
    .max(100, "Muito longo!")
    .required("Obrigatório"),
  prompt: Yup.string()
    .min(50, "Muito curto!")
    .required("Descreva o treinamento para Inteligência Artificial"),
  model: Yup.string()
    .oneOf(openAiModels, "Modelo inválido")
    .required("Informe o modelo"),
  maxTokens: Yup.number()
    .min(10, "Mínimo 10 tokens")
    .max(4000, "Máximo 4000 tokens")
    .required("Informe o número máximo de tokens"),
  temperature: Yup.number()
    .min(0, "Mínimo 0")
    .max(2, "Máximo 2")
    .required("Informe a temperatura"),
  apiKey: Yup.string(),
  maxMessages: Yup.number()
    .min(1, "Mínimo 1 mensagem")
    .max(100, "Máximo 100 mensagens")
    .required("Informe o número máximo de mensagens"),
  replyDelayMinMs: Yup.number().integer().min(0).max(MAX_REPLY_DELAY_MS).required(),
  replyDelayMaxMs: Yup.number().integer().min(Yup.ref("replyDelayMinMs"), "Deve ser maior ou igual ao mínimo").max(MAX_REPLY_DELAY_MS).required(),
  blockGapMinMs: Yup.number().integer().min(0).max(15000).required(),
  blockGapMaxMs: Yup.number().integer().min(Yup.ref("blockGapMinMs"), "Deve ser maior ou igual ao mínimo").max(15000).required(),
  voice: Yup.string().required("Informe o modo para Voz"),
  voiceKey: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Key é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  voiceRegion: Yup.string().when("voice", {
    is: (voice) => voice !== "texto",
    then: Yup.string().required("Voice Region é obrigatória para síntese de voz"),
    otherwise: Yup.string().notRequired()
  }),
  flowMode: Yup.string()
    .oneOf(["permanent", "temporary"], "Modo de fluxo inválido")
    .required("Selecione o modo de fluxo"),
  maxInteractions: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      .min(1, "Mínimo 1 interação")
      .max(50, "Máximo 50 interações")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  completionTimeout: Yup.number().when("flowMode", {
    is: "temporary",
    then: Yup.number()
      .min(1, "Mínimo 1 minuto")
      .max(60, "Máximo 60 minutos")
      .nullable(),
    otherwise: Yup.number().nullable()
  }),
  continueKeywords: Yup.array().when("flowMode", {
    is: "temporary",
    then: Yup.array()
      .of(Yup.string().required("Palavra-chave não pode estar vazia"))
      .min(1, "Pelo menos uma palavra-chave é obrigatória no modo temporário"),
    otherwise: Yup.array()
  }),
  objective: Yup.string().when(["flowMode", "autoCompleteOnObjective"], {
    is: (flowMode, autoComplete) => flowMode === "temporary" && autoComplete,
    then: Yup.string().required("Objetivo é obrigatório quando auto completar está ativo"),
    otherwise: Yup.string()
  })
});

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    prompt: "",
    model: "gpt-4.1-mini",
    voice: "texto",
    voiceKey: "",
    voiceRegion: "",
    maxTokens: 4000,
    temperature: 0.3,
    apiKey: "",
    maxMessages: 8,
    queueId: 0,
    replyDelayMinMs: 3000,
    replyDelayMaxMs: 3000,
    blockGapMinMs: 1200,
    blockGapMaxMs: 2400,
    
    // Campos para controle de fluxo
    flowMode: "permanent",
    maxInteractions: 5,
    completionTimeout: 10,
    continueKeywords: ["continuar", "próximo", "avançar"],
    objective: "",
    autoCompleteOnObjective: false
  };

  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar Estratiza AI ao fluxo",
    btn: "Adicionar",
  });
  const [newKeyword, setNewKeyword] = useState("");
  const [planHasAi, setPlanHasAi] = useState(false);
  const [savedAgents, setSavedAgents] = useState([]);
  const [savingAgent, setSavingAgent] = useState(false);
  const [saveAgentName, setSaveAgentName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [aiFiles, setAiFiles] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [uploadFileName, setUploadFileName] = useState("");
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const [availableQueues, setAvailableQueues] = useState([]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Estratiza AI do fluxo",
        btn: "Salvar",
      });
      const typebotIntegration = data?.data?.typebotIntegration || {};
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        model: openAiModels.includes(typebotIntegration.model)
          ? typebotIntegration.model
          : "gpt-4o",
        flowMode: typebotIntegration.flowMode || "permanent",
        continueKeywords: typebotIntegration.continueKeywords || ["continuar", "próximo", "avançar"],
        maxInteractions: typebotIntegration.maxInteractions || 5,
        completionTimeout: typebotIntegration.completionTimeout || 10,
        objective: typebotIntegration.objective || "",
        autoCompleteOnObjective: typebotIntegration.autoCompleteOnObjective || false,
        replyDelayMinMs: typebotIntegration.replyDelayMinMs ?? 3000,
        replyDelayMaxMs: typebotIntegration.replyDelayMaxMs ?? 3000,
        blockGapMinMs: typebotIntegration.blockGapMinMs ?? 1200,
        blockGapMaxMs: typebotIntegration.blockGapMaxMs ?? 2400
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Estratiza AI ao fluxo",
        btn: "Adicionar",
      });
      setIntegration(initialState);
      setCurrentAgentId(null);
    }

    if (open) { loadSavedAgents(); loadAiFiles(); }
    return () => {
      isMounted.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data]);

  useEffect(() => {
    if (!user?.companyId) return;
    api.get("/companies/listPlan/" + user.companyId).then(({ data: d }) => {
      const limit = d?.plan?.aiTokensLimit || 0;
      setPlanHasAi(limit > 0);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    api.get("/queue").then(({ data }) => {
      setAvailableQueues(Array.isArray(data) ? data : []);
    }).catch(() => {});
  }, []);

  const loadAiFiles = async () => {
    try {
      const { data: d } = await api.get("/ai-agent-files");
      setAiFiles(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
  };

  const [uploadProgress, setUploadProgress] = useState(0);

  const handleAiFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!uploadFileName.trim()) {
      const { toast } = await import("react-toastify");
      toast.warning("Preencha o nome do material antes de escolher o arquivo.");
      return;
    }
    // Reset input so mesmo arquivo pode ser selecionado novamente
    e.target.value = "";
    setFileUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", uploadFileName.trim());
      await api.post("/ai-agent-files", formData, {
        timeout: 0, // sem timeout para arquivos grandes
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
          setUploadProgress(pct);
        }
      });
      setUploadFileName("");
      setUploadProgress(0);
      const { toast } = await import("react-toastify");
      toast.success(`Arquivo "${file.name}" enviado com sucesso!`);
      loadAiFiles();
    } catch (err) {
      setUploadProgress(0);
      const { toast } = await import("react-toastify");
      const msg = err?.response?.data?.error || err?.message || "Erro ao enviar arquivo";
      toast.error(`Erro no upload: ${msg}`);
      console.error(err);
    } finally {
      setFileUploading(false);
    }
  };

  const handleDeleteAiFile = async (id) => {
    if (!window.confirm("Remover este arquivo?")) return;
    try {
      await api.delete(`/ai-agent-files/${id}`);
      loadAiFiles();
    } catch (e) { console.error(e); }
  };

  const loadSavedAgents = async () => {
    try {
      const { data: d } = await api.get("/ai-agents");
      setSavedAgents(Array.isArray(d) ? d : []);
    } catch (e) { console.error(e); }
  };

  const handleLoadAgent = (agentId, setFieldValue) => {
    const agent = savedAgents.find(a => String(a.id) === String(agentId));
    if (!agent) return;
    setCurrentAgentId(agentId);
    const fields = ["name","prompt","model","apiKey","maxTokens","temperature",
      "voice","voiceKey","voiceRegion","maxMessages","queueId","flowMode",
      "maxInteractions","continueKeywords","completionTimeout","objective","autoCompleteOnObjective",
      "replyDelayMinMs","replyDelayMaxMs","blockGapMinMs","blockGapMaxMs"];
    fields.forEach(field => {
      if (agent[field] !== undefined && agent[field] !== null) {
        let val = agent[field];
        if (field === "continueKeywords" && typeof val === "string") {
          try { val = JSON.parse(val); } catch(e2) {}
        }
        setFieldValue(field, val);
      }
    });
  };

  const handleSaveAgent = async (values) => {
    if (!saveAgentName.trim()) return;
    setSavingAgent(true);
    try {
      const payload = { ...values, name: saveAgentName.trim() };
      if (Array.isArray(payload.continueKeywords))
        payload.continueKeywords = JSON.stringify(payload.continueKeywords);
      await api.post("/ai-agents", payload);
      const { toast } = await import("react-toastify");
      toast.success("Agente salvo com sucesso!");
      setShowSaveDialog(false);
      setSaveAgentName("");
      loadSavedAgents();
    } catch (e) { console.error(e); }
    finally { setSavingAgent(false); }
  };

  const handleClose = () => {
    setNewKeyword("");
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const promptData = {
      ...values,
      agentId: currentAgentId || null,
      // Garantir que campos do modo temporário sejam nulos se modo for permanente
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      // Forçar provider como openai
      provider: "openai"
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { typebotIntegration: promptData },
      });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({
        typebotIntegration: promptData,
      });
    }
    handleClose();
    setSubmitting(false);
  };

  const addKeyword = (arrayHelpers, keyword) => {
    if (keyword.trim() && !integration.continueKeywords.includes(keyword.trim())) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  const removeKeyword = (arrayHelpers, index) => {
    arrayHelpers.remove(index);
  };

  const getModelDisplayName = (model) => {
    const modelNames = {
      "gpt-3.5-turbo": "GPT 3.5 Turbo",
      "gpt-3.5-turbo-1106": "GPT 3.5 Turbo (1106)",
      "gpt-3.5-turbo-16k": "GPT 3.5 Turbo 16K",
      "gpt-4": "GPT 4",
      "gpt-4-turbo": "GPT 4 Turbo",
      "gpt-4o": "GPT 4o",
      "gpt-4o-mini": "GPT 4o Mini",
      "gpt-4.1": "GPT 4.1",
      "gpt-4.1-mini": "GPT 4.1 Mini (recomendado)",
      "gpt-4.1-nano": "GPT 4.1 Nano"
    };
    return modelNames[model] || model;
  };

  const getVoiceDisplayName = (voice) => {
    if (voice === "texto") return "Apenas Texto";
    return voice.replace("pt-BR-", "").replace("Neural", "");
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        onMouseDown={e => e.stopPropagation()}
      >
        <DialogTitle id="form-dialog-title">
          {labels.title}
        </DialogTitle>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={OpenAiSchema}
          onSubmit={handleSavePrompt}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent dividers>
                
                {/* CONFIGURAÇÕES BÁSICAS */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Settings />
                      Configurações Básicas
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <div style={{ marginBottom: 14, padding: "12px 14px",
                      background: "rgba(1,187,172,0.05)", border: "1px solid rgba(1,187,172,0.25)", borderRadius: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#01BBAC" }}>&#129302; Usar Agente Salvo</span>
                        <span style={{ fontSize: 11, color: "#aaa" }}>(opcional)</span>
                      </div>
                      {savedAgents.length === 0 ? (
                        <span style={{ fontSize: 12, color: "#aaa" }}>Nenhum agente salvo ainda. Configure e clique em "Salvar Agente".</span>
                      ) : (
                        <>
                          <select defaultValue=""
                            onChange={e => handleLoadAgent(e.target.value, setFieldValue)}
                            style={{ width: "100%", fontSize: 13, padding: "7px 10px", borderRadius: 6,
                              border: "1px solid rgba(1,187,172,0.4)", color: "#333", background: "#fff", cursor: "pointer" }}>
                            <option value="">-- Selecione um agente para carregar --</option>
                            {savedAgents.map(a => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                          </select>
                          <div style={{ fontSize: 11, color: "#777", marginTop: 5 }}>Ao selecionar, todos os campos serão preenchidos com as configurações do agente salvo.</div>
                        </>
                      )}
                    </div>

                    <Field
                      as={TextField}
                      label="Nome do Assistente"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                    />

                    <div style={{ padding: "10px 12px", background: "#e8f5e9",
                      borderRadius: 6, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: "#2e7d32", fontSize: 18 }}>&#10003;</span>
                      <div>
                        <div style={{ color: "#2e7d32", fontWeight: 600, fontSize: 13 }}>Estratiza AI inclusa no seu plano</div>
                        <div style={{ color: "#555", fontSize: 12 }}>Seu agente de IA é gerenciado automaticamente. Não é necessário configurar.</div>
                      </div>
                    </div>

                    <Field
                      as={TextField}
                      label="Prompt do Sistema"
                      name="prompt"
                      error={touched.prompt && Boolean(errors.prompt)}
                      helperText={touched.prompt && errors.prompt}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                      rows={6}
                      multiline
                      placeholder="Descreva como a IA deve se comportar, que informações deve coletar, como deve responder..."
                    />

                    <div className={classes.multFieldLine}>
                      <Field
                        as={TextField}
                        label="Temperatura"
                        name="temperature"
                        error={touched.temperature && Boolean(errors.temperature)}
                        helperText={touched.temperature && errors.temperature || "0 = conservador, 2 = criativo"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                        inputProps={{
                          step: "0.1",
                          min: "0",
                          max: "2",
                        }}
                      />
                      <Field
                        as={TextField}
                        label="Max Tokens"
                        name="maxTokens"
                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                        helperText={touched.maxTokens && errors.maxTokens || "Tamanho máximo da resposta"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                      <Field
                        as={TextField}
                        label="Max Mensagens"
                        name="maxMessages"
                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                        helperText={touched.maxMessages && errors.maxMessages || "Histórico de contexto"}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        type="number"
                      />
                    </div>

                    {/* AI-COORDINATION-ITEMS-1-2-14: delays pertencem ao agente. */}
                    <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                      Antes de responder ao cliente (HH:MM:SS)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Chegou nova mensagem: a contagem reinicia.
                    </Typography>
                    <div className={classes.multFieldLine}>
                      <Field as={TextField} label="Espera mínima" name="replyDelayMinMs"
                        type="time" variant="outlined" margin="dense" fullWidth
                        inputProps={{ min: "00:00:00", max: "23:59:59", step: 1 }}
                        value={replyDelayToClock(values.replyDelayMinMs)}
                        onChange={e => setFieldValue("replyDelayMinMs", clockToReplyDelay(e.target.value))}
                        error={touched.replyDelayMinMs && Boolean(errors.replyDelayMinMs)}
                        helperText={touched.replyDelayMinMs && errors.replyDelayMinMs || "Menor espera após a última mensagem."} />
                      <Field as={TextField} label="Espera máxima" name="replyDelayMaxMs"
                        type="time" variant="outlined" margin="dense" fullWidth
                        inputProps={{ min: "00:00:00", max: "23:59:59", step: 1 }}
                        value={replyDelayToClock(values.replyDelayMaxMs)}
                        onChange={e => setFieldValue("replyDelayMaxMs", clockToReplyDelay(e.target.value))}
                        error={touched.replyDelayMaxMs && Boolean(errors.replyDelayMaxMs)}
                        helperText={touched.replyDelayMaxMs && errors.replyDelayMaxMs || "Maior espera; igual ao mínimo = fixo."} />
                    </div>
                    <Typography variant="subtitle2" style={{ marginTop: 12 }}>
                      Entre os blocos da resposta (HH:MM:SS)
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Pausa somente entre partes da mesma resposta.
                    </Typography>
                    <div className={classes.multFieldLine}>
                      <Field as={TextField} label="Intervalo mínimo" name="blockGapMinMs"
                        type="time" variant="outlined" margin="dense" fullWidth
                        inputProps={{ min: "00:00:00", max: "00:00:15", step: 1 }}
                        value={blockGapToClock(values.blockGapMinMs)}
                        onChange={e => setFieldValue("blockGapMinMs", clockToBlockGap(e.target.value))}
                        error={touched.blockGapMinMs && Boolean(errors.blockGapMinMs)}
                        helperText={touched.blockGapMinMs && errors.blockGapMinMs || "Menor pausa, em segundos."} />
                      <Field as={TextField} label="Intervalo máximo" name="blockGapMaxMs"
                        type="time" variant="outlined" margin="dense" fullWidth
                        inputProps={{ min: "00:00:00", max: "00:00:15", step: 1 }}
                        value={blockGapToClock(values.blockGapMaxMs)}
                        onChange={e => setFieldValue("blockGapMaxMs", clockToBlockGap(e.target.value))}
                        error={touched.blockGapMaxMs && Boolean(errors.blockGapMaxMs)}
                        helperText={touched.blockGapMaxMs && errors.blockGapMaxMs || "Maior pausa; igual ao mínimo = fixa."} />
                    </div>

                  </AccordionDetails>
                </Accordion>

                {/* CONFIGURAÇÕES DE VOZ */}
                <Accordion className={classes.accordion}>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Chat />
                      Configurações de Voz (Opcional)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      error={touched.voice && Boolean(errors.voice)}
                    >
                      <InputLabel>Tipo de Resposta</InputLabel>
                      <Field
                        as={Select}
                        label="Tipo de Resposta"
                        name="voice"
                      >
                        {availableVoices.map((voice) => (
                          <MenuItem key={voice} value={voice}>
                            {getVoiceDisplayName(voice)}
                          </MenuItem>
                        ))}
                      </Field>
                      {touched.voice && errors.voice && (
                        <div style={{ color: "red", fontSize: "12px" }}>
                          {errors.voice}
                        </div>
                      )}
                    </FormControl>

                    {values.voice !== "texto" && (
                      <div className={classes.multFieldLine}>
                        <Field
                          as={TextField}
                          label="Voice Key (Azure)"
                          name="voiceKey"
                          error={touched.voiceKey && Boolean(errors.voiceKey)}
                          helperText={touched.voiceKey && errors.voiceKey}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          placeholder="Chave do Azure Speech Service"
                        />
                        <Field
                          as={TextField}
                          label="Voice Region (Azure)"
                          name="voiceRegion"
                          error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                          helperText={touched.voiceRegion && errors.voiceRegion}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          placeholder="ex: brazilsouth"
                        />
                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* COMPORTAMENTO DO FLUXO */}
                <Accordion className={classes.accordion} defaultExpanded>
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className={classes.accordionSummary}
                  >
                    <Typography className={classes.sectionTitle}>
                      <Timer />
                      Comportamento do Fluxo
                      <Tooltip title="Configure como o OpenAI deve se comportar no fluxo">
                        <Info fontSize="small" color="action" />
                      </Tooltip>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    
                    <FormControl component="fieldset" margin="normal">
                      <FormLabel component="legend">Modo de Funcionamento</FormLabel>
                      <RadioGroup
                        name="flowMode"
                        value={values.flowMode}
                        onChange={(e) => setFieldValue("flowMode", e.target.value)}
                      >
                        <FormControlLabel
                          value="permanent"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Permanente</strong> - Encerrar fluxo aqui
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                O usuário fica conversando com a IA até pedir transferência ou encerrar
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="temporary"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1">
                                <strong>Temporário</strong> - Volta ao fluxo depois
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA executa uma tarefa específica e depois retorna ao fluxo normal
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* CONFIGURAÇÕES DO MODO TEMPORÁRIO */}
                    {values.flowMode === "temporary" && (
                      <div className={classes.temporarySettings}>
                        <Typography variant="h6" gutterBottom>
                          ⏱️ Configurações do Modo Temporário
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Configure quando a IA deve parar e retornar ao fluxo
                        </Typography>

                        {/* Limite de Interações */}
                        <Field
                          as={TextField}
                          label="Máximo de Interações"
                          name="maxInteractions"
                          error={touched.maxInteractions && Boolean(errors.maxInteractions)}
                          helperText={touched.maxInteractions && errors.maxInteractions || "Número máximo de mensagens antes de voltar ao fluxo (0 = ilimitado)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 50 }}
                        />

                        {/* Timeout */}
                        <Field
                          as={TextField}
                          label="Timeout (minutos)"
                          name="completionTimeout"
                          error={touched.completionTimeout && Boolean(errors.completionTimeout)}
                          helperText={touched.completionTimeout && errors.completionTimeout || "Tempo limite antes de voltar ao fluxo (0 = sem limite)"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, max: 60 }}
                        />

                        {/* Palavras-chave de Continuação */}
                        <FormControl fullWidth margin="dense">
                          <Typography variant="subtitle2" gutterBottom>
                            Palavras-chave para Continuar Fluxo
                          </Typography>
                          <FieldArray name="continueKeywords">
                            {(arrayHelpers) => (
                              <div>
                                <Box display="flex" gap={1} alignItems="center" mb={1}>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Digite uma palavra-chave"
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === "Enter") {
                                        addKeyword(arrayHelpers, newKeyword);
                                      }
                                    }}
                                  />
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Add />}
                                    onClick={() => addKeyword(arrayHelpers, newKeyword)}
                                    disabled={!newKeyword.trim()}
                                  >
                                    Adicionar
                                  </Button>
                                </Box>
                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                  {values.continueKeywords.map((keyword, index) => (
                                    <Chip
                                      key={index}
                                      label={keyword}
                                      className={classes.keywordChip}
                                      onDelete={() => removeKeyword(arrayHelpers, index)}
                                      deleteIcon={<Delete />}
                                      variant="outlined"
                                      size="small"
                                    />
                                  ))}
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  Quando o usuário enviar uma dessas palavras, o fluxo continuará automaticamente
                                </Typography>
                                {touched.continueKeywords && errors.continueKeywords && (
                                  <Typography variant="caption" color="error">
                                    {errors.continueKeywords}
                                  </Typography>
                                )}
                              </div>
                            )}
                          </FieldArray>
                        </FormControl>

                        {/* Objetivo */}
                        <Field
                          as={TextField}
                          label="Objetivo da IA"
                          name="objective"
                          error={touched.objective && Boolean(errors.objective)}
                          helperText={touched.objective && errors.objective || "Descreva o que a IA deve completar (ex: 'Coletar nome, email e telefone')"}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                          multiline
                          rows={2}
                          placeholder="Ex: Coletar dados do cliente, Qualificar interesse, Diagnosticar problema..."
                        />

                        {/* Auto Completar */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.autoCompleteOnObjective}
                              onChange={(e) => setFieldValue("autoCompleteOnObjective", e.target.checked)}
                              name="autoCompleteOnObjective"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2">
                                Auto completar quando atingir objetivo
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                A IA analisará automaticamente se completou o objetivo e voltará ao fluxo
                              </Typography>
                            </Box>
                          }
                        />

                        {/* Fila de Transferência */}
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel>Fila de Transferência</InputLabel>
                          <Field
                            as={Select}
                            name="queueId"
                            label="Fila de Transferência"
                            onChange={e => setFieldValue("queueId", Number(e.target.value))}
                            value={values.queueId || 0}
                          >
                            <MenuItem value={0}><em>Nenhuma (não transferir)</em></MenuItem>
                            {availableQueues.map(q => (
                              <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                            ))}
                          </Field>
                        </FormControl>

                      </div>
                    )}

                  </AccordionDetails>
                </Accordion>

                {/* MATERIAIS PARA ENVIO */}
                <div style={{ marginTop: 18, padding: "14px 16px", border: "1px solid rgba(1,187,172,0.25)", borderRadius: 8, background: "rgba(1,187,172,0.03)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#01BBAC", marginBottom: 4 }}>
                    Materiais para Envio
                  </div>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 10, lineHeight: 1.6 }}>
                    Adicione PDFs, áudios, imagens ou vídeos que a IA poderá enviar automaticamente durante a conversa.
                    No prompt, utilize a tag:{" "}
                    <code style={{ background: "#f5f5f5", padding: "1px 6px", borderRadius: 3, fontSize: 11, color: "#333" }}>[ENVIAR:Nome do Material]</code>
                  </div>
                  <div style={{ fontSize: 10, color: "#888", background: "#fffde7", border: "1px solid #fff9c4", borderRadius: 5, padding: "5px 10px", marginBottom: 10, lineHeight: 1.6 }}>
                    <strong>Exemplo no prompt:</strong>{" "}"Quando o cliente pedir o cardápio, inclua [ENVIAR:Cardápio] na sua resposta."
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
                    <input
                      value={uploadFileName}
                      onChange={e => setUploadFileName(e.target.value)}
                      placeholder="Nome do material (ex: Cardápio)"
                      style={{ flex: 1, minWidth: 150, fontSize: 12, padding: "5px 10px", borderRadius: 5, border: "1px solid #ccc", outline: "none" }}
                    />
                    <label style={{
                      display: "inline-flex", alignItems: "center",
                      fontSize: 12, padding: "5px 14px", borderRadius: 5,
                      border: "1px solid rgba(1,187,172,0.5)", color: "#01BBAC",
                      cursor: (fileUploading || !uploadFileName.trim()) ? "not-allowed" : "pointer",
                      background: "#fff", fontWeight: 600,
                      opacity: (fileUploading || !uploadFileName.trim()) ? 0.5 : 1
                    }}>
                      {fileUploading ? (uploadProgress > 0 && uploadProgress < 100 ? `Enviando ${uploadProgress}%...` : "Enviando...") : "Escolher arquivo"}
                      <input type="file" hidden
                        accept="audio/*,video/*,image/*,.pdf,.doc,.docx,.xls,.xlsx"
                        onChange={handleAiFileUpload}
                        disabled={fileUploading || !uploadFileName.trim()}
                      />
                    </label>
                  </div>
                  <div style={{ fontSize: 10, color: "#aaa", marginBottom: 10 }}>Dê um nome antes de escolher o arquivo. Suporta vídeo, áudio, imagem, PDF e documentos (máx 200MB).</div>
                  {fileUploading && uploadProgress > 0 && (
                    <div style={{ width: "100%", background: "#e0e0e0", borderRadius: 4, height: 6, marginBottom: 8 }}>
                      <div style={{ width: `${uploadProgress}%`, background: "#01BBAC", borderRadius: 4, height: 6, transition: "width 0.3s ease" }} />
                    </div>
                  )}
                  {aiFiles.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {aiFiles.map(f => (
                        <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#fff", borderRadius: 6, border: "1px solid #e8e8e8" }}>
                          <span style={{ fontSize: 12, color: "#888", minWidth: 36 }}>
                            {f.mimetype && f.mimetype.startsWith("audio") ? "audio" : f.mimetype && f.mimetype.startsWith("image") ? "img" : f.mimetype && f.mimetype.startsWith("video") ? "video" : "doc"}
                          </span>
                          <span style={{ flex: 1, fontSize: 12, color: "#333", fontWeight: 600 }}>{f.name}</span>
                          <code style={{ fontSize: 10, color: "#777", background: "#f5f5f5", padding: "1px 6px", borderRadius: 3, whiteSpace: "nowrap" }}>[ENVIAR:{f.name}]</code>
                          <button onClick={() => handleDeleteAiFile(f.id)}
                            style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "none", background: "#ffebee", color: "#c62828", cursor: "pointer", fontWeight: 700 }}
                          >X</button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 11, color: "#aaa" }}>Nenhum material adicionado ainda.</div>
                  )}
                </div>

              </DialogContent>
              {showSaveDialog && (
            <div style={{ padding: "14px 24px 12px", background: "#f0faf9", borderTop: "2px solid #01BBAC" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#01BBAC", marginBottom: 8 }}>Salvar agente na biblioteca</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <input autoFocus value={saveAgentName}
                  onChange={e => setSaveAgentName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleSaveAgent(values); }}
                  placeholder="Dê um nome para este agente (ex: Agente Comercial)"
                  style={{ flex: 1, minWidth: 200, fontSize: 13, padding: "7px 12px",
                    borderRadius: 6, border: "1px solid #80cbc4", outline: "none" }}
                />
                <button onClick={() => handleSaveAgent(values)}
                  disabled={savingAgent || !saveAgentName.trim()}
                  style={{ fontSize: 13, padding: "7px 18px", borderRadius: 6, border: "none", fontWeight: 700,
                    color: "#fff", cursor: (savingAgent || !saveAgentName.trim()) ? "default" : "pointer",
                    background: (savingAgent || !saveAgentName.trim()) ? "#ccc" : "#01BBAC" }}>
                  {savingAgent ? "Salvando..." : "Confirmar"}
                </button>
                <button onClick={() => { setShowSaveDialog(false); setSaveAgentName(""); }}
                  style={{ fontSize: 13, padding: "7px 14px", borderRadius: 6,
                    border: "1px solid #ccc", background: "#fff", cursor: "pointer", color: "#666" }}>
                  Cancelar
                </button>
              </div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 8, lineHeight: 1.6 }}>
                Ao salvar, este agente ficará disponível no menu <strong>Agentes de IA</strong> e poderá ser reutilizado em qualquer fluxo sem precisar configurar novamente.
              </div>
            </div>
          )}
          <DialogActions style={{ padding: "10px 20px", gap: 6 }}>
            <Button onClick={handleClose} color="secondary" variant="outlined" disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="button"
              onClick={() => { setShowSaveDialog(s => !s); setSaveAgentName(values.name || ""); }}
              variant="contained"
              style={{ background: showSaveDialog ? "#00897b" : "#01BBAC", color: "#fff", fontWeight: 700, boxShadow: "none" }}>
              {showSaveDialog ? "Fechar" : "Salvar Agente"}
            </Button>
            <Button type="submit" color="primary" variant="contained"
              className={classes.btnWrapper} disabled={isSubmitting}>
              {labels.btn}
            </Button>
          </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderOpenAIModal;
